import { Condition } from "./condition";
import { promiseStreamRun, promiseStreamThrottle } from "./promise-stream";
import { Queue } from "./queue";

/**
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/fromAsync Array.fromAsync}
 */
export async function asyncIterableToArray<T>(
  iterable: AsyncIterable<T>,
): Promise<T[]> {
  const result: T[] = [];
  for await (const item of iterable) {
    result.push(item);
  }
  return result;
}

export async function* asyncIterableMap<A, B>(
  iterable: AsyncIterable<A>,
  f: (a: A) => B | Promise<B>,
): AsyncIterable<B> {
  for await (const item of iterable) {
    yield f(item);
  }
}

export class BufferOverflowError extends Error {
  constructor() {
    super("Buffer overflow");
  }
}

export async function* asyncIterableBuffer<T>(
  iterable: AsyncIterable<T>,
  limit: number,
): AsyncIterable<T> {
  const [writable, asyncIterable] = writeableAsyncIterable<T>();
  let size = 0;
  const produce = (async () => {
    try {
      for await (const item of iterable) {
        if (limit <= ++size) {
          throw new BufferOverflowError();
        }
        writable.push(item);
      }
    } finally {
      writable.end();
    }
  })();
  produce.catch(() => {});

  try {
    for await (const item of asyncIterable) {
      size--;
      yield item;
    }
  } finally {
    await produce;
  }
}

export async function* asyncIterableIgnoreAbort<T>(
  iterable: AsyncIterable<T>,
  abort: AbortSignal,
): AsyncIterable<T> {
  try {
    for await (const item of iterable) {
      yield item;
    }
  } catch (e) {
    if (abort.aborted) {
      return;
    }
    throw e;
  }
}

export function asyncIterableMerge<T>(...iterables: AsyncIterable<T>[]) {
  if (iterables.length === 1) {
    return iterables[0];
  }
  return (async function* () {
    const [writable, asyncIterable] = writeableAsyncIterable<T>();
    const promise = Promise.all(
      iterables.map(async (iterable) => {
        for await (const item of iterable) {
          writable.push(item);
        }
      }),
    );
    promise.catch(() => {});
    try {
      for await (const item of asyncIterable) {
        yield item;
      }
    } finally {
      await promise;
    }
  })();
}

export function lazyAsyncIterable<T>(
  fn: () => AsyncIterable<T>,
): AsyncIterable<T> {
  return { [Symbol.asyncIterator]: () => fn()[Symbol.asyncIterator]() };
}

export async function parallelForEach<T>(
  iterable: Iterable<T> | AsyncIterable<T>,
  concurrency: number,
  fn: (item: T) => Promise<void>,
) {
  for await (const _ of parallelMapUnordered(iterable, concurrency, fn)) {
    /**/
  }
}

export function parallelMapUnordered<A, B>(
  iterable: Iterable<A> | AsyncIterable<A>,
  concurrency: number,
  fn: (item: A) => Promise<B>,
): AsyncIterableIterator<B> {
  const promises = async function* () {
    for await (const element of iterable) {
      yield () => fn(element);
    }
  };
  return promiseStreamRun(promiseStreamThrottle(promises(), concurrency));
}

export async function* slice<T>(
  iterator: AsyncIterable<T>,
  start: number,
  end: number,
): AsyncIterableIterator<T> {
  if (end <= start) {
    return;
  }
  let index = 0;
  for await (const item of iterator) {
    if (start <= index) {
      yield item;
    }
    if (end <= ++index) {
      break;
    }
  }
}

export async function* while_<T>(
  iterator: AsyncIterable<T>,
  condition: (item: T) => boolean,
) {
  for await (const item of iterator) {
    if (!condition(item)) {
      break;
    }
    yield item;
  }
}

export interface Writable<T> {
  push(item: T): void;
  end(): void;
}

export function writeableAsyncIterable<T>(): [Writable<T>, AsyncIterable<T>] {
  let condition: Condition<void> | undefined = undefined;
  let done = false;
  const queue = new Queue<T>();

  const writable: Writable<T> = {
    push(item) {
      if (done) {
        return;
      }
      queue.push(item);
      condition?.resolve();
    },
    end() {
      done = true;
      condition?.resolve();
    },
  };

  const iterable = (async function* () {
    try {
      while (true) {
        if (!queue.empty()) {
          yield queue.pop();
        } else if (done) {
          break;
        } else {
          condition = new Condition<void>();
          await condition.result;
          condition = undefined;
        }
      }
    } finally {
      done = true;
    }
  })();

  return [writable, iterable];
}
